import React, { Component } from 'react';
import AmCharts from '@amcharts/amcharts3-react';
import FlatButton from 'material-ui/FlatButton';
import TextField from 'material-ui/TextField';
import Cookies from 'js-cookie';
import moment from 'moment';

import { API_ROOT } from '../api/apiConfig';
import Loading from '../components/Loading';

const PRESETS = [
  { id: '24h', label: '24 h' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'Month' },
  { id: '3month', label: '3 months' },
  { id: 'custom', label: 'Custom' },
];

const wrap = {
  padding: '8px 4px 24px',
  maxWidth: 1200,
  margin: '0 auto',
};

const toolbar = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
  padding: '12px 16px',
  background: 'linear-gradient(180deg, #fafbfc 0%, #f4f6f8 100%)',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 8,
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};

const titleStyle = {
  margin: '0 8px 0 0',
  fontSize: 18,
  fontWeight: 500,
  color: 'rgba(0,0,0,0.75)',
};

const metaStyle = {
  fontSize: 12,
  color: 'rgba(0,0,0,0.45)',
  marginLeft: 'auto',
  paddingLeft: 12,
};

const chartShell = {
  background: '#fff',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 8,
  padding: '8px 4px 4px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};

const spikePanelStyle = {
  marginTop: 16,
  padding: 16,
  background: '#fff',
  border: '1px solid rgba(0,0,0,0.1)',
  borderRadius: 8,
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
};

class ConferenceStats extends Component {
  constructor(props) {
    super(props);
    // Stable refs required: @amcharts/amcharts3-react removes listeners when `method`
    // identity changes across re-renders, which breaks click handlers after data loads.
    this.onClickGraphItem = this.onClickGraphItem.bind(this);
    this.onChartCursorSelected = this.onChartCursorSelected.bind(this);
    this.spikePanelRef = null;
    const now = moment();
    this.state = {
      loading: true,
      preset: '24h',
      customFrom: now.clone().subtract(7, 'days').format('YYYY-MM-DDTHH:mm'),
      customTo: now.format('YYYY-MM-DDTHH:mm'),
      data: [],
      meta: null,
      error: null,
      spikeDialogOpen: false,
      spikeLoading: false,
      spikeError: null,
      spikeData: null,
      spikeBucketLabel: '',
    };
  }

  componentDidMount() {
    this.fetchStats();
  }

  fetchStats() {
    const csrftoken = Cookies.get('csrftoken');
    const { preset, customFrom, customTo } = this.state;

    const body = { preset };
    if (preset === 'custom') {
      body.datetime_from = moment(customFrom, 'YYYY-MM-DDTHH:mm').toISOString();
      body.datetime_to = moment(customTo, 'YYYY-MM-DDTHH:mm').toISOString();
    }

    this.setState({ loading: true, error: null });

    fetch(`${API_ROOT}/bridges/conference-stats/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRFToken': csrftoken,
      },
      body: JSON.stringify(body),
    })
      .then((response) => response.text().then((text) => {
        let payload = null;
        if (text) {
          try {
            payload = JSON.parse(text);
          } catch (e) {
            throw new Error(response.statusText || 'Invalid JSON from server');
          }
        }
        if (!response.ok) {
          throw new Error(
            (payload && (payload.error || payload.detail)) || response.statusText || 'Request failed',
          );
        }
        return payload;
      }))
      .then((payload) => {
        if (!payload || Number(payload.status) !== 200) {
          throw new Error((payload && payload.error) || 'Request failed');
        }
        this.setState({
          data: payload.data || [],
          meta: payload.meta || null,
          loading: false,
        });
      })
      .catch((err) => {
        this.setState({
          loading: false,
          error: err.message || 'Failed to load stats',
          data: [],
          meta: null,
        });
      });
  }

  fetchSpikeCompanies(snapshotId, bucketLabel) {
    const csrftoken = Cookies.get('csrftoken');
    this.setState(
      {
        spikeDialogOpen: true,
        spikeLoading: true,
        spikeError: null,
        spikeData: null,
        spikeBucketLabel: bucketLabel || '',
      },
      () => this.scrollSpikePanelIntoView(),
    );

    fetch(`${API_ROOT}/bridges/conference-stats-spike-companies/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRFToken': csrftoken,
      },
      body: JSON.stringify({ snapshot_id: snapshotId }),
    })
      .then((response) => response.text().then((text) => {
        let payload = null;
        if (text) {
          try {
            payload = JSON.parse(text);
          } catch (e) {
            throw new Error(response.statusText || 'Invalid JSON from server');
          }
        }
        if (!response.ok) {
          throw new Error(
            (payload && (payload.error || payload.detail)) || response.statusText || 'Request failed',
          );
        }
        return payload;
      }))
      .then((payload) => {
        if (!payload || Number(payload.status) !== 200) {
          throw new Error((payload && payload.error) || 'Request failed');
        }
        this.setState(
          {
            spikeLoading: false,
            spikeData: payload.data || null,
          },
          () => this.scrollSpikePanelIntoView(),
        );
      })
      .catch((err) => {
        this.setState(
          {
            spikeLoading: false,
            spikeError: err.message || 'Failed to load spike detail',
          },
          () => this.scrollSpikePanelIntoView(),
        );
      });
  }

  scrollSpikePanelIntoView() {
    if (this.spikePanelRef && typeof this.spikePanelRef.scrollIntoView === 'function') {
      this.spikePanelRef.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  closeSpikeDialog() {
    this.setState({ spikeDialogOpen: false });
  }

  setPreset(preset) {
    this.setState({ preset }, () => {
      if (preset !== 'custom') {
        this.fetchStats();
      }
    });
  }

  applyCustom() {
    this.fetchStats();
  }

  onClickGraphItem(e) {
    const threshold = (this.state.meta && this.state.meta.spike_threshold) || 30;
    const ctx = e.item && e.item.dataContext;
    if (!ctx || ctx.snapshot_id == null) return;
    if ((Number(ctx.calls) || 0) <= threshold) return;
    this.fetchSpikeCompanies(ctx.snapshot_id, ctx.datetime);
  }

  onChartCursorSelected(e) {
    const chart = e.chart;
    const threshold = (this.state.meta && this.state.meta.spike_threshold) || 30;
    if (!chart || !chart.dataProvider || typeof e.startIndex !== 'number' || typeof e.endIndex !== 'number') {
      return;
    }
    const lo = Math.min(e.startIndex, e.endIndex);
    const hi = Math.max(e.startIndex, e.endIndex);
    let best = null;
    for (let i = lo; i <= hi; i += 1) {
      const ctx = chart.dataProvider[i];
      if (!ctx) continue;
      const calls = Number(ctx.calls) || 0;
      if (ctx.snapshot_id == null || calls <= threshold) continue;
      if (!best || calls > best.calls) {
        best = { snapshot_id: ctx.snapshot_id, calls, datetime: ctx.datetime };
      }
    }
    if (best) {
      this.fetchSpikeCompanies(best.snapshot_id, `${best.datetime} (selected range)`);
    } else {
      this.setState(
        {
          spikeDialogOpen: true,
          spikeLoading: false,
          spikeError:
            `No bucket in that range exceeds ${threshold} FreeSwitch concurrent callers with drill-down data. Drag across a spike, or click a visible point on the line.`,
          spikeData: null,
          spikeBucketLabel: 'Selection',
        },
        () => this.scrollSpikePanelIntoView(),
      );
    }
  }

  buildChartConfig(rows) {
    const count = rows.length;
    const labelFrequency = count > 16 ? Math.max(1, Math.ceil(count / 14)) : 1;
    const threshold = (this.state.meta && this.state.meta.spike_threshold) || 30;

    return {
      type: 'serial',
      theme: 'light',
      addClassNames: true,
      fontFamily: 'Inter, "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: 11,
      color: '#555',
      autoMargins: false,
      marginLeft: 48,
      marginRight: 20,
      marginTop: 12,
      marginBottom: 56,
      dataProvider: rows,
      startDuration: 0.25,
      legend: { enabled: false },
      listeners: [
        {
          event: 'clickGraphItem',
          method: this.onClickGraphItem,
        },
      ],
      chartCursor: {
        cursorAlpha: 0.12,
        fullWidth: true,
        categoryBalloonEnabled: true,
        categoryBalloonAlpha: 0.92,
        // Drag selects index range for company drill-down; do not zoom the chart.
        zoomable: true,
        selectWithoutZooming: true,
        listeners: [
          {
            event: 'selected',
            method: this.onChartCursorSelected,
          },
        ],
      },
      balloon: {
        adjustBorderColor: false,
        horizontalPadding: 10,
        verticalPadding: 8,
        color: '#e6edf3',
        fillColor: '#161b22',
        borderThickness: 0,
      },
      valueAxes: [
        {
          id: 'v1',
          axisAlpha: 0.2,
          gridAlpha: 0.06,
          title: 'FS callers',
          titleFontSize: 12,
          integersOnly: true,
          minimum: 0,
        },
      ],
      graphs: [
        {
          title: 'FreeSwitch',
          type: 'smoothedLine',
          valueField: 'calls',
          lineThickness: 2,
          lineColor: '#22c55e',
          bullet: 'round',
          bulletSizeField: 'bullet',
          bulletHitAreaSize: 16,
          showHandOnHover: true,
          bulletBorderAlpha: 0.4,
          bulletAlpha: 0.85,
          fillAlphas: 0.12,
          fillColors: '#22c55e',
          balloonText:
            '[[category]]<br><b>[[value]]</b> FS concurrent'
            + '<br><span style="font-size:11px;opacity:0.85">Click or drag-select for companies when above '
            + `${threshold}</span>`,
        },
      ],
      categoryField: 'datetime',
      categoryAxis: {
        gridPosition: 'start',
        axisAlpha: 0.12,
        gridAlpha: 0.05,
        tickLength: 3,
        labelRotation: count > 12 ? 35 : 0,
        autoWrap: false,
        labelFrequency,
        minHorizontalGap: 0,
        twoLineMode: false,
      },
    };
  }

  render() {
    if (this.state.loading && !this.state.meta && this.state.data.length === 0) {
      return <Loading />;
    }

    const {
      preset,
      customFrom,
      customTo,
      data,
      meta,
      error,
      loading,
      spikeDialogOpen,
      spikeLoading,
      spikeError,
      spikeData,
      spikeBucketLabel,
    } = this.state;
    const threshold = (meta && meta.spike_threshold) || 30;
    const rows = (data || []).map((d) => {
      const calls = Number(d.calls != null ? d.calls : d.callers) || 0;
      const snapshotId = d.snapshot_id != null ? d.snapshot_id : null;
      return {
        datetime: d.datetime != null ? String(d.datetime) : '',
        calls,
        snapshot_id: snapshotId,
        bullet: snapshotId && calls > threshold ? 5 : 0,
      };
    });

    const bucketHint = meta && meta.bucket_minutes
      ? `Buckets: ${meta.bucket_minutes >= 60 ? `${meta.bucket_minutes / 60} h` : `${meta.bucket_minutes} min`}`
      : '';

    return (
      <div style={wrap}>
        <div style={toolbar}>
          <span style={titleStyle}>Conference stats (FreeSwitch)</span>
          {PRESETS.map((p) => (
            <FlatButton
              key={p.id}
              label={p.label}
              primary={preset === p.id}
              onClick={() => this.setPreset(p.id)}
              style={{ minWidth: 72 }}
            />
          ))}
          {preset === 'custom' && (
            <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                hintText="From"
                type="datetime-local"
                value={customFrom}
                onChange={(e) => this.setState({ customFrom: e.target.value })}
                style={{ width: 200, marginRight: 8 }}
              />
              <TextField
                hintText="To"
                type="datetime-local"
                value={customTo}
                onChange={(e) => this.setState({ customTo: e.target.value })}
                style={{ width: 200, marginRight: 8 }}
              />
              <FlatButton label="Apply" primary onClick={() => this.applyCustom()} />
            </span>
          )}
          {meta && (
            <span style={metaStyle}>
              {bucketHint}
              {meta.datetime_from && meta.datetime_to && (
                <span>
                  {' '}
                  · {moment(meta.datetime_from).format('MMM D, HH:mm')} —{' '}
                  {moment(meta.datetime_to).format('MMM D, HH:mm')}
                </span>
              )}
            </span>
          )}
        </div>

        {error && (
          <div
            style={{
              color: '#c62828',
              marginBottom: 12,
              padding: 12,
              background: '#ffebee',
              borderRadius: 4,
            }}
          >
            {error}
          </div>
        )}

        {loading && (
          <div style={{ padding: 16, textAlign: 'center', color: 'rgba(0,0,0,0.45)' }}>
            Loading…
          </div>
        )}

        <div
          style={{
            fontSize: 12,
            color: 'rgba(0,0,0,0.5)',
            marginBottom: 8,
            paddingLeft: 4,
          }}
        >
          {`Peak FreeSwitch concurrent callers per bucket (from snapshots). Click a point, or drag horizontally across categories (FS > ${threshold}). Company names load in the panel below the chart (not inside the chart). "Show all" only resets chart zoom.`}
        </div>

        <div style={chartShell}>
          {rows.length === 0 && !loading ? (
            <div
              style={{
                height: 480,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(0,0,0,0.38)',
                fontSize: 15,
              }}
            >
              No data in this range.
            </div>
          ) : (
            <AmCharts.React
              style={{ width: '100%', height: 480 }}
              options={this.buildChartConfig(rows)}
            />
          )}
        </div>

        {spikeDialogOpen && (
          <div
            ref={(el) => {
              this.spikePanelRef = el;
            }}
            style={spikePanelStyle}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 12,
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 500, color: 'rgba(0,0,0,0.82)' }}>
                Company breakdown
                {spikeBucketLabel ? ` — ${spikeBucketLabel}` : ''}
              </span>
              <FlatButton label="Dismiss" onClick={() => this.closeSpikeDialog()} />
            </div>

            {spikeLoading && (
              <div style={{ padding: 16, textAlign: 'center', color: 'rgba(0,0,0,0.5)' }}>
                Loading…
              </div>
            )}
            {spikeError && (
              <div style={{ color: '#c62828', padding: '4px 0' }}>{spikeError}</div>
            )}
            {!spikeLoading && !spikeError && spikeData && (
              <div style={{ padding: '4px 0' }}>
                <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)', marginBottom: 12 }}>
                  Snapshot {spikeData.snapshot_id}
                  {spikeData.created && (
                    <span>
                      {' '}
                      · {moment(spikeData.created).format('MMM D, YYYY HH:mm:ss')}
                    </span>
                  )}
                  {spikeData.freeswitch_calls != null && (
                    <span>
                      {' '}
                      · FS total {spikeData.freeswitch_calls}
                    </span>
                  )}
                </div>
                {(spikeData.companies || []).length === 0 ? (
                  <div style={{ color: 'rgba(0,0,0,0.45)' }}>
                    No conference ids on this snapshot, or none resolved to a company.
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', borderBottom: '1px solid #eee' }}>
                        <th style={{ padding: '8px 4px' }}>Company</th>
                        <th style={{ padding: '8px 4px' }}>FS slots</th>
                        <th style={{ padding: '8px 4px' }}>Conferences</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(spikeData.companies || []).map((c, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f5f5f5' }}>
                          <td style={{ padding: '8px 4px' }}>{c.name}</td>
                          <td style={{ padding: '8px 4px' }}>{c.caller_slots}</td>
                          <td style={{ padding: '8px 4px' }}>{c.conference_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {(spikeData.unknown_conference_ids || []).length > 0 && (
                  <div style={{ marginTop: 12, fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
                    Unresolved conference ids: {spikeData.unknown_conference_ids.join(', ')}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default ConferenceStats;



// WEBPACK FOOTER //
// ./src/pages/ConferenceStats.js